import { ErrorBanner, HelpBanner, StrategyCallModal } from '@/components';
import { HelpItem } from '@/components/HelpBanner';
import Layout from '@/components/Layout';
import { PATH } from '@/constants';
import { AccountPlan, Subscription } from '@/constants/enum';
import { capitalizeFirstLetter } from '@/helpers';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import {
  useUpgradeTracking,
  EntryType,
  PLAN_NAME_MAP,
  usePlanDisplayName,
  useStrategyCallEligibility,
  useDiscountCodeDeepLink,
} from '@/hooks';
import { useGetUserPlanQuery, useUpdatePlanMutation } from '@/redux/api/api.caller';
import { handleToast } from '@/redux/features/common.slice';
import { BlockStack, Box, Card, Grid, SkeletonBodyText, SkeletonDisplayText } from '@shopify/polaris';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import OverQuotaBanner from '../Dashboard/components/OverQuotaBanner';
import AppUsage from './components/AppUsage';
import DiscountCode, { DiscountCodeRef } from './components/DiscountCode';
import PlanCard from './components/PlanCard';
import TrialBanner from './components/TrialBanner';
import QnASection from './components/QnASection';
import QuotaSlider from './components/QuotaSlider';
import {
  getExtraQuotaFromTotalQuotaPerMonth,
  getMonthlyQuantity,
  getRangeQuotaForAllPlans,
  getSubscriptionText,
  sortedPlansByTier,
  UNLIMITED_QUOTA,
} from './config';
import { StyledBillingOption, StyledBillingSwitcher, StyledBundlePill, StyledSubscriptionPlans } from './styled';

interface SubscriptionPlanProps {
  isUpgradePlan?: boolean;
}

// TR-480: nội dung khớp Figma 4987-16144 (banner "Not sure which plan fits or need custom price?").
const customHelpItems: HelpItem[] = [
  { id: '1', label: 'We will review requests individually:', showBullet: false },
  { id: '2', label: 'Offer a special discount, quota, trial period.', showBullet: true },
  { id: '3', label: 'Find suitable solutions for your fulfillment needs.', showBullet: true },
];

const SubscriptionPlans = ({ isUpgradePlan = false }: SubscriptionPlanProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const discountCodeRef = useRef<DiscountCodeRef>(null);
  const pendingCodeInfo = useDiscountCodeDeepLink();
  const [pendingDiscountCode, setPendingDiscountCode] = useState<{ code: string; autoApply: boolean } | null>(null);
  const [totalQuota, setTotalQuota] = useState<number>(0);
  const [selectedPlan, setSelectedPlan] = useState<AccountPlan>();
  const [subscription, setSubscription] = useState<Subscription>(Subscription.Monthly);
  const [apiError, setApiError] = useState<string>('');
  const { data, isLoading } = useGetUserPlanQuery({});
  const [updatePlan, { isLoading: isUpdatePlanLoading }] = useUpdatePlanMutation();
  const dispatch = useDispatch();
  const { captureEvent } = useUpgradeTracking();
  const { getPlanName } = usePlanDisplayName();
  // TR-480: nút "1:1 Strategy Call" trong banner "Need a custom solution?" chỉ cho shop eligible.
  const { isEligible: isStrategyCallEligible } = useStrategyCallEligibility();
  const [isStrategyModalOpen, setIsStrategyModalOpen] = useState(false);
  const forwardedRef = useRef<{ source_page: string; entry_type: EntryType; feature: string | null }>({
    source_page: 'direct',
    entry_type: 'direct',
    feature: null,
  });
  const quotaRange = getRangeQuotaForAllPlans(data?.data.allPlans || [], subscription);
  useEffect(() => {
    if (new URLSearchParams(location.search).get('planError') === 'usage_pricing_changed') {
      setApiError("We couldn't confirm your subscription due to a billing sync issue. Please choose your plan again.");
    }
  }, [location.search]);
  const resetApiError = () => {
    if (apiError) {
      setApiError('');
    }
  };

  const handleChangeSubscription = (subscription: Subscription) => {
    resetApiError();
    setSubscription(subscription);
  };

  const checkBtnLoading = (plan: AccountPlan): boolean => isUpdatePlanLoading && selectedPlan === plan;

  const handleUpdatePlan = (plan: AccountPlan) => {
    // Fire plan_selected BEFORE mutation (intent event — not success).
    const fallbackPlanName = PLAN_NAME_MAP[plan];
    const planName = fallbackPlanName ? getPlanName(plan) || fallbackPlanName : undefined;
    if (planName) {
      captureEvent('plan_selected', {
        plan_name: planName,
        billing_period: subscription,
        source_page: forwardedRef.current.source_page,
        entry_type: forwardedRef.current.entry_type,
      });
    }
    // (If plan = starter → planName undefined → skip event, do NOT block mutation)

    const extraQuotaPerMonth = getExtraQuotaFromTotalQuotaPerMonth(plan, subscription, quotaRange, totalQuota);
    setSelectedPlan(plan);
    updatePlan({
      plan,
      subscription,
      extraQuotaPerMonth,
    }).then((res) => {
      if ('data' in res) {
        if ('data' in res && res.data && res.data.data) {
          window.open(res.data.data?.confirmationUrl, '_blank');
        } else {
          dispatch(handleToast(handleToastMutation(res)));
        }
        resetApiError();
        return;
      }
      setApiError(getErrorFromAPI(res));
    });
  };

  const renderedSubscriptionPlan = useMemo(() => {
    if (data) {
      const { allPlans } = data.data;

      return allPlans.map((planInfo) => {
        const previousPlan =
          planInfo.plan === AccountPlan.Starter ? null : sortedPlansByTier[sortedPlansByTier.indexOf(planInfo.plan) - 1];
        const previousPlanInfo = previousPlan ? allPlans.find((item) => item.plan === previousPlan) : null;

        return (
          <Grid.Cell
            key={planInfo.id}
            columnSpan={{
              xl: 1,
              lg: 1,
              md: 1,
              sm: 1,
              xs: 1,
            }}
          >
            <PlanCard
              previousDisplayPlan={previousPlanInfo ? previousPlanInfo.displayName : null}
              planInfo={planInfo}
              activeSubscription={subscription}
              handleUpdatePlan={() => handleUpdatePlan(planInfo.plan)}
              totalQuota={totalQuota}
              isBtnLoading={checkBtnLoading(planInfo.plan)}
              isUpgradePlan={isUpgradePlan}
            />
          </Grid.Cell>
        );
      });
    }
    return new Array(sortedPlansByTier.length).fill(0).map((_, index) => (
      <Grid.Cell
        key={index}
        columnSpan={{
          xl: 1,
          lg: 1,
          md: 1,
          sm: 1,
          xs: 1,
        }}
      >
        <Card>
          <SkeletonBodyText lines={33} />
        </Card>
      </Grid.Cell>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, totalQuota, subscription, checkBtnLoading, isUpgradePlan]);

  const annuallyDiscountContent = useMemo(() => {
    if (data) {
      const allPlans = data.data.allPlans;
      const MONTHS = 12;
      const minAnnualMultiplier = allPlans.reduce(
        (min, plan) => (plan.annualMultiplier < min ? plan.annualMultiplier : min),
        Infinity,
      );
      const saveMonthsQuantity = MONTHS - minAnnualMultiplier;
      return `save ${saveMonthsQuantity} month${saveMonthsQuantity > 1 ? 's' : ''}`;
    }
    return '';
  }, [data]);

  useLayoutEffect(() => {
    if (data) {
      // set quota value
      const { quotaPerMonth, extraQuotaPerMonth, plan } = data.data;
      if (quotaPerMonth !== UNLIMITED_QUOTA) {
        const monthQuantity = getMonthlyQuantity(plan, subscription);

        const quotaPerMonthValue = Number(quotaPerMonth) * monthQuantity;
        const extraQuotaPerMonthValue = extraQuotaPerMonth * monthQuantity;
        setTotalQuota(quotaPerMonthValue + extraQuotaPerMonthValue);
      }
    }
  }, [data, subscription]);

  useEffect(() => {
    if (data) {
      // set subscription
      if (data.data.plan === AccountPlan.Starter) {
        setSubscription(Subscription.Monthly);
      } else {
        setSubscription(data.data.subscription);
      }
    }
  }, [data]);

  const handleApplyDiscountCode = useCallback(
    (discountCode: string, autoApply: boolean) => {
      if (discountCodeRef.current) {
        discountCodeRef.current.toggleModal();
        discountCodeRef.current.setDiscount({ code: discountCode, error: '' });
        if (autoApply) {
          setTimeout(() => {
            discountCodeRef.current?.handleApplyDiscountCode?.();
          }, 500);
        }
      }
    },
    [discountCodeRef],
  );

  // Mount-once: read forwarded state, fire plan_page_viewed, capture state for plan_selected, clear state
  useEffect(() => {
    const stateFromNav = (location.state ?? {}) as Partial<{
      source_page: string;
      entry_type: EntryType;
      feature: string;
    }>;

    forwardedRef.current = {
      source_page: stateFromNav.source_page ?? 'direct',
      entry_type: stateFromNav.entry_type ?? (pendingCodeInfo?.source === 'query' ? 'email_promo' : 'direct'),
      feature: stateFromNav.feature ?? null,
    };

    captureEvent('plan_page_viewed', {
      referrer_page: forwardedRef.current.source_page,
      entry_type: forwardedRef.current.entry_type,
    });

    if (pendingCodeInfo) {
      setPendingDiscountCode({ code: pendingCodeInfo.code, autoApply: pendingCodeInfo.autoApply });
    }

    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply once data is loaded and ref is ready
  useEffect(() => {
    if (pendingDiscountCode && data && discountCodeRef.current) {
      handleApplyDiscountCode(pendingDiscountCode.code, pendingDiscountCode.autoApply);
      setPendingDiscountCode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDiscountCode, data]);

  return (
    <StyledSubscriptionPlans $isUpgradePlan={isUpgradePlan}>
      <Layout title="Subscription Plans" titleHidden={isUpgradePlan} maxWidth="80rem" fullWidth>
        <TrialBanner />
        {data?.data && data.data.plan !== AccountPlan.Starter && (
          <Box paddingBlockEnd="400">
            <AppUsage />
          </Box>
        )}
        <OverQuotaBanner />
        <BlockStack gap="400">
          <ErrorBanner title={isUpgradePlan ? '' : 'Subscription plans error'} isVisible={!!apiError}>
            {apiError}
          </ErrorBanner>
          <div className="subscription-button">
            {isLoading || !data ? (
              <div className="subscription-button-skeleton">
                <SkeletonDisplayText size="medium" />
              </div>
            ) : (
              <StyledBillingSwitcher>
                {Object.entries(Subscription).map(([key, value]) => (
                  <StyledBillingOption
                    key={value}
                    type="button"
                    $active={subscription === value}
                    onClick={() => handleChangeSubscription(value)}
                  >
                    {`${capitalizeFirstLetter(getSubscriptionText(value, value === Subscription.Annually))}${
                      value === Subscription.Annually ? ` (${annuallyDiscountContent})` : ''
                    }`}
                  </StyledBillingOption>
                ))}
                <StyledBundlePill type="button" onClick={() => navigate(PATH.PLAN_BUNDLE)}>
                  Post-purchase bundle (save 30%)
                </StyledBundlePill>
              </StyledBillingSwitcher>
            )}
          </div>

          <DiscountCode ref={discountCodeRef} annuallyDiscountContent={annuallyDiscountContent} />

          {data && data.data.quotaPerMonth !== UNLIMITED_QUOTA && (
            <QuotaSlider subscription={subscription} totalQuota={totalQuota} setTotalQuota={setTotalQuota} />
          )}

          <Box paddingBlockStart="300">
            <Grid
              columns={{
                xl: isUpgradePlan ? 3 : 4,
                lg: isUpgradePlan ? 3 : 4,
                md: 2,
                sm: 1,
                xs: 1,
              }}
              gap={{ xs: '1.5rem' }}
            >
              {renderedSubscriptionPlan}
            </Grid>
          </Box>

          <HelpBanner
            id="261bd11a-4045-482d-a69d-9ff0477c1084"
            title="Not sure which plan fits or need custom price?"
            helpItems={customHelpItems}
            isStrategyCallEligible={isStrategyCallEligible}
            onStrategyCall={() => setIsStrategyModalOpen(true)}
          />

          <QnASection />
        </BlockStack>
      </Layout>
      <StrategyCallModal open={isStrategyModalOpen} onClose={() => setIsStrategyModalOpen(false)} />
    </StyledSubscriptionPlans>
  );
};

export default memo(SubscriptionPlans);
